import type { z } from 'zod';

/**
 * Reading and writing localStorage, validated.
 *
 * localStorage is user-writable and survives deploys, so what comes back out of
 * it is untrusted input like any request body: a value written by a build from
 * six months ago, or by hand in devtools, has to be rejected rather than fed
 * into the UI. Every read goes through a Zod schema and falls back to a known
 * default on anything unexpected.
 *
 * Writes are best-effort. Private browsing and a full quota both throw, and
 * losing a preference is not worth taking the app down for.
 */

export function readPersisted<T>(key: string, schema: z.ZodType<T>, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;

    const parsed = schema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : fallback;
  } catch {
    // Unavailable storage, or JSON that isn't.
    return fallback;
  }
}

export function writePersisted<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Persisting is a nicety, not a requirement.
  }
}

export function clearPersisted(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // As above.
  }
}
